import { createTheme, type PaletteMode } from "@mui/material";

const lightTokens = {
	primary: {
		main: "#10b981",
		light: "#34d399",
		dark: "#059669",
		contrastText: "#ffffff",
	},
	secondary: {
		main: "#64748b",
		light: "#94a3b8",
		dark: "#475569",
		contrastText: "#ffffff",
	},
	background: { default: "#ecfdf5", paper: "#e6f4ef" },
	divider: "#d1fae5",
};

const darkTokens = {
	primary: {
		main: "#34d399",
		light: "#6ee7b7",
		dark: "#10b981",
		contrastText: "#0b1412",
	},
	secondary: {
		main: "#94a3b8",
		light: "#cbd5e1",
		dark: "#64748b",
		contrastText: "#0b1412",
	},
	background: { default: "#0b1412", paper: "#0f1f1a" },
	divider: "#1f2937",
};

export default function createAppTheme(mode: PaletteMode = "light") {
	const isDark = mode === "dark";
	const p = isDark ? darkTokens : lightTokens;
	return createTheme({
		palette: {
			mode,
			primary: p.primary,
			secondary: p.secondary,
			background: p.background,
			divider: p.divider,
			success: { main: "#22c55e" },
			error: { main: "#ef4444" },
			warning: { main: "#f59e0b" },
			info: { main: "#0ea5e9" },
		},
		shape: { borderRadius: 10 },
		typography: {
			fontFamily:
				"Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
			h1: { fontWeight: 700 },
			h2: { fontWeight: 700 },
			h3: { fontWeight: 700 },
			h4: { fontWeight: 700 },
			h5: { fontWeight: 700 },
			h6: { fontWeight: 700 },
			button: { textTransform: "none", fontWeight: 600 },
		},
		components: {
			MuiButton: {
				defaultProps: { disableElevation: true },
				styleOverrides: {
					root: {
						borderRadius: 10,
						paddingInline: 14,
						transition:
							"transform 160ms ease, box-shadow 160ms ease, background-color 120ms ease, border-color 120ms ease",
						"&:hover": { transform: "translateY(-1px)" },
						"&:active": { transform: "translateY(0) scale(0.99)" },
						"&.Mui-focusVisible": {
							outline: `2px solid ${isDark ? "rgba(148, 163, 184, 0.5)" : "rgba(14, 165, 233, 0.45)"}`,
							outlineOffset: 2,
						},
					},
					contained: {
						boxShadow: isDark
							? "0 8px 20px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.25)"
							: "0 8px 18px rgba(16,185,129,0.18), 0 2px 6px rgba(0,0,0,0.06)",
						"&:hover": {
							boxShadow: isDark
								? "0 12px 28px rgba(0,0,0,0.4), 0 3px 10px rgba(0,0,0,0.28)"
								: "0 12px 26px rgba(16,185,129,0.22), 0 3px 10px rgba(0,0,0,0.08)",
						},
					},
					outlined: {
						borderWidth: 1.5,
						"&:hover": {
							borderWidth: 1.5,
							backgroundColor: isDark
								? "rgba(255,255,255,0.06)"
								: "rgba(0,0,0,0.03)",
						},
					},
					text: {
						"&:hover": {
							backgroundColor: isDark
								? "rgba(255,255,255,0.06)"
								: "rgba(0,0,0,0.03)",
						},
					},
				},
			},
			MuiPaper: {
				defaultProps: { elevation: 1 },
				styleOverrides: {
					root: {
						borderRadius: 10,
						backgroundColor: isDark
							? "rgba(13, 26, 22, 0.36)"
							: "rgba(255, 255, 255, 0.36)",
						backdropFilter: "saturate(140%) blur(12px)",
						WebkitBackdropFilter: "saturate(140%) blur(12px)",
						border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
						boxShadow: isDark
							? "0 8px 24px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.25)"
							: "0 8px 24px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						borderRadius: 12,
						backgroundColor: isDark
							? "rgba(13, 26, 22, 0.36)"
							: "rgba(255, 255, 255, 0.36)",
						backdropFilter: "saturate(140%) blur(12px)",
						WebkitBackdropFilter: "saturate(140%) blur(12px)",
						border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
						boxShadow: isDark
							? "0 8px 24px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.25)"
							: "0 8px 24px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
					},
				},
			},
			MuiAppBar: {
				styleOverrides: {
					root: {
						borderRadius: 0,
						backgroundColor: isDark
							? "rgba(11, 20, 18, 0.45)"
							: "rgba(255,255,255,0.55)",
						backdropFilter: "saturate(140%) blur(14px)",
						WebkitBackdropFilter: "saturate(140%) blur(14px)",
						borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
						boxShadow: isDark
							? "0 10px 24px rgba(0,0,0,0.35)"
							: "0 10px 24px rgba(0,0,0,0.06)",
					},
				},
			},
			MuiDrawer: {
				styleOverrides: {
					paper: {
						backgroundColor: isDark
							? "rgba(13, 26, 22, 0.36)"
							: "rgba(255,255,255,0.36)",
						backdropFilter: "saturate(140%) blur(12px)",
						WebkitBackdropFilter: "saturate(140%) blur(12px)",
						borderRight: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						backgroundColor: isDark
							? "rgba(13, 26, 22, 0.4)"
							: "rgba(255, 255, 255, 0.6)",
						backdropFilter: "saturate(140%) blur(16px)",
						WebkitBackdropFilter: "saturate(140%) blur(16px)",
						borderRadius: 12,
						border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						backgroundColor: isDark
							? "rgba(255,255,255,0.03)"
							: "rgba(255,255,255,0.8)",
						borderRadius: 10,
						transition:
							"box-shadow 160ms ease, background-color 120ms ease, border-color 120ms ease",
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: isDark
								? "rgba(148,163,184,0.45)"
								: "rgba(17,94,89,0.35)",
						},
						"&.Mui-focused": {
							backgroundColor: isDark
								? "rgba(255,255,255,0.05)"
								: "rgba(255,255,255,0.95)",
						},
					},
					notchedOutline: {
						borderColor: isDark
							? "rgba(148,163,184,0.28)"
							: "rgba(20,83,45,0.22)",
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						borderRadius: 8,
						backgroundColor: isDark
							? "rgba(15,23,42,0.92)"
							: "rgba(2,6,23,0.9)",
						color: "#f8fafc",
						backdropFilter: "saturate(140%) blur(6px)",
						WebkitBackdropFilter: "saturate(140%) blur(6px)",
					},
					arrow: { color: isDark ? "rgba(15,23,42,0.92)" : "rgba(2,6,23,0.9)" },
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						scrollbarColor: "#cbd5e1 #f1f5f9",
						backgroundColor: p.background.default,
						backgroundImage: [
							// base tint
							isDark
								? "linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0) 100%)"
								: "linear-gradient(180deg, rgba(16,185,129,0.10) 0%, rgba(59,130,246,0.10) 100%)",
							// primary blob (top-left)
							isDark
								? "radial-gradient(1000px 520px at -10% -20%, rgba(16,185,129,0.16), transparent)"
								: "radial-gradient(1000px 520px at -10% -20%, rgba(16,185,129,0.36), transparent)",
							// secondary blob (bottom-right)
							isDark
								? "radial-gradient(820px 480px at 120% 120%, rgba(59,130,246,0.12), transparent)"
								: "radial-gradient(820px 480px at 120% 120%, rgba(2,132,199,0.30), transparent)",
							// conic depth sweep
							isDark
								? "conic-gradient(from 210deg at 60% 8%, rgba(148,163,184,0.06), transparent 55%, rgba(148,163,184,0.04))"
								: "conic-gradient(from 210deg at 60% 8%, rgba(99,102,241,0.14), transparent 50%, rgba(34,197,94,0.12))",
							// diagonal highlight band
							isDark
								? "linear-gradient(135deg, transparent 30%, rgba(148,163,184,0.10) 45%, transparent 60%)"
								: "linear-gradient(135deg, transparent 28%, rgba(16,185,129,0.16) 45%, transparent 62%)",
							// ultra-subtle grid (light only)
							isDark
								? "linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)"
								: "linear-gradient(90deg, rgba(0,0,0,0.025) 1px, transparent 1px)",
							isDark
								? "linear-gradient(0deg, rgba(255,255,255,0.03) 1px, transparent 1px)"
								: "linear-gradient(0deg, rgba(0,0,0,0.02) 1px, transparent 1px)",
						].join(","),
						backgroundRepeat:
							"no-repeat, no-repeat, no-repeat, no-repeat, no-repeat, repeat, repeat",
						backgroundAttachment:
							"fixed, fixed, fixed, fixed, fixed, fixed, fixed",
						backgroundSize:
							"100% 100%, auto, auto, 160% 160%, 140% 140%, 8px 8px, 8px 8px",
					},
					"*::-webkit-scrollbar": { width: 8, height: 8 },
					"*::-webkit-scrollbar-thumb": {
						backgroundColor: "#cbd5e1",
						borderRadius: 8,
					},
					"*::-webkit-scrollbar-track": { backgroundColor: "#f1f5f9" },
				},
			},
		},
	});
}
